<script lang="ts">
	import { getContext, setContext } from "svelte";
	import { get, writable, type Writable } from "svelte/store";
	import type { BrandKit } from "#gen/brands/v1/service_pb.js";
	import { AssetType, RenderMode, type AssetRef } from "#gen/bundles/v1/bundle_pb.js";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import type { AssetLoad } from "#lib/api/bundles.js";
	import { cloneTokenProviderForNode } from "#lib/components/flow/editor/builder/utils.js";
	import AdUnit from "#lib/components/flow/viewer/AdUnit.svelte";
	import ModuleButton from "#lib/components/flow/viewer/ModuleButton.svelte";
	import ModuleInline from "#lib/components/flow/viewer/ModuleInline.svelte";
	import PageContainer from "#lib/components/flow/viewer/PageContainer.svelte";
	import PolicyContentViewer from "#lib/components/flow/viewer/PolicyContentViewer.svelte";
	import type { ModuleItemProps } from "#lib/components/flow/viewer/types.js";
	import { isPassthroughType } from "#lib/components/flow/viewer/utils.js";
	import Page from "#lib/components/page/Page.svelte";
	import type { BundleDag } from "#lib/dag.js";
	import { styleWithBrandKit, type Themeable } from "#lib/theme.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";

	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");
	const brandKit: Writable<BrandKit | undefined> = getContext("brandKit");
	const dag: Writable<BundleDag<AssetRef>> = getContext("dag");
	const assets: Writable<Record<string, AssetLoad>> = getContext("assets");

	const { suite, module, metadata, layoutName, themeStyle, isLightBackground, isConversionModule, actionEnabled, mode, classes, readOnly, interpolate, onShow, onEdit, onExecute, onCancel }: ModuleItemProps = $props();
	const asset = $derived($assets[module.key]);
	const scopedTokenProvider: Writable<TokenProvider> = writable(cloneTokenProviderForNode($tokenProvider, $dag, $assets, module.key));

	$effect(() => {
		$scopedTokenProvider = cloneTokenProviderForNode($tokenProvider, $dag, $assets, module.key);
	});

	function handleEdit() {
		if (asset) {
			onEdit?.(asset);
		}
	}

	function getOverrideStyle(store: Writable<Themeable> | undefined): string {
		if (!store) {
			return themeStyle;
		}
		const themeable = get(store);
		if (themeable?.themeOverrides) {
			return styleWithBrandKit($brandKit, themeable);
		}
		return "--background-color: white;";
	}

	setContext("tokenProvider", scopedTokenProvider);
</script>

<div role="presentation" onclick={mode === Mode.EDITING ? handleEdit : undefined}>
	{#if module.ref?.type === AssetType.PAGE_V2}
		<Page class={classes.inlineClass} {asset} themeStyle={getOverrideStyle(asset.asPage())} parentMlRequestId={metadata.mlRequestId} renderMode={RenderMode.INLINE} {mode} {readOnly} {interpolate} {onExecute} {onCancel} modal />
	{:else if module.ref?.type === AssetType.PAGE_CONTAINER}
		<PageContainer {asset} dag={$dag} {metadata} themeStyle={getOverrideStyle(asset.asPageContainer())} {isLightBackground} {mode} {readOnly} {interpolate} {onShow} {onExecute} {onCancel} modal />
	{:else if module.ref?.type === AssetType.POLICY || module.ref?.type === AssetType.POLICY_REF}
		<PolicyContentViewer {asset} {metadata} {onCancel} />
	{:else if module.ref?.type === AssetType.AD_UNIT}
		<AdUnit class={classes.inlineClass} {asset} {mode} />
	{:else if isPassthroughType(module.ref?.type)}
		<ModuleInline {suite} {module} {metadata} {themeStyle} {isLightBackground} {mode} {classes} {interpolate} {readOnly} {onShow} {onEdit} {onExecute} {onCancel} />
	{:else}
		<ModuleButton {suite} {module} {metadata} {layoutName} {mode} {themeStyle} {isLightBackground} {classes} {interpolate} {isConversionModule} {actionEnabled} {onShow} {onEdit} {readOnly} />
	{/if}
</div>
